import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Rocket, Calendar, ExternalLink, TrendingUp, FileText, Bell, BellRing, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const prettyDate = (s) => {
  if (!s) return "—";
  const [mm, dd, yy] = s.split("/");
  if (!mm || !dd || !yy) return s;
  const d = new Date(Number(yy), Number(mm) - 1, Number(dd));
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const fmtShares = (n) => n == null ? "—" : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : `${(n / 1e3).toFixed(0)}k`;

export default function IPOs() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [q, setQ] = useState("");
  const [alerts, setAlerts] = useState({ alerts: [], hot: [] });

  // Day-1 paper buy dialog
  const [buyRow, setBuyRow] = useState(null);
  const [buyQty, setBuyQty] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [buyStop, setBuyStop] = useState("15");
  const [buying, setBuying] = useState(false);

  const loadIPOs = () => api.get("/ipos").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], recent: [], filed: [], counts: {} })).finally(() => setLoading(false));
  const loadAlerts = () => api.get("/ipos/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => {
    let alive = true;
    loadIPOs(); loadAlerts();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAlert = async (row) => {
    if (row.is_watched) {
      await api.delete("/ipos/alerts", { params: { ticker: row.ticker || "", company: row.company || "" } });
      toast.success(`Removed alert for ${row.ticker || row.company}`);
    } else {
      await api.post("/ipos/alerts", {
        ticker: row.ticker || "", company: row.company || "",
        expected_date: row.expected_date || row.priced_date || "",
      });
      toast.success(`We'll ping you when ${row.ticker || row.company} prices`);
    }
    // Optimistically flip flag locally
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const k of ["upcoming", "recent", "filed"]) {
        next[k] = (prev[k] || []).map((r) => r === row ? { ...r, is_watched: !r.is_watched } : r);
      }
      return next;
    });
    loadAlerts();
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const src = data[tab] || [];
    const term = q.trim().toLowerCase();
    return term ? src.filter((r) => (r.ticker + " " + r.company + " " + (r.exchange || "")).toLowerCase().includes(term)) : src;
  }, [data, tab, q]);

  return (
    <div className="space-y-6" data-testid="ipos-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Market Debuts</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-3">
          <Rocket className="w-7 h-7 md:w-8 md:h-8 opacity-70" />
          IPO Calendar
        </h1>
        <div className="text-sm text-muted-foreground mt-1">
          Fresh listings, upcoming pricings and freshly filed deals, straight from Nasdaq's public calendar.
        </div>
      </div>

      {/* Hot alerts banner — IPOs the user is watching pricing today / tomorrow */}
      {alerts.hot?.length > 0 && (
        <Card className="p-4 rounded-xl border-[hsl(var(--ai))]/30 bg-[hsl(var(--ai))]/10" data-testid="hot-alerts-banner">
          <div className="flex items-start gap-3">
            <BellRing className="w-5 h-5 text-[hsl(var(--ai))] mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium">Your watchlist is heating up</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {alerts.hot.map((h, i) => (
                  <span key={i} className="mr-3">
                    <span className="font-mono-tab text-foreground">{h.ticker || h.company}</span>
                    <span className="ml-1">{h.trigger === "today" ? "prices today" : "prices tomorrow"}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 rounded-xl border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="ipos-tabs">
              <TabsTrigger value="upcoming" data-testid="ipos-tab-upcoming">
                <Calendar className="w-3.5 h-3.5 mr-1" />
                Upcoming {data?.counts?.upcoming != null && <span className="ml-1 text-muted-foreground">({data.counts.upcoming})</span>}
              </TabsTrigger>
              <TabsTrigger value="recent" data-testid="ipos-tab-recent">
                <TrendingUp className="w-3.5 h-3.5 mr-1" />
                Recently priced {data?.counts?.recent != null && <span className="ml-1 text-muted-foreground">({data.counts.recent})</span>}
              </TabsTrigger>
              <TabsTrigger value="filed" data-testid="ipos-tab-filed">
                <FileText className="w-3.5 h-3.5 mr-1" />
                Filed {data?.counts?.filed != null && <span className="ml-1 text-muted-foreground">({data.counts.filed})</span>}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Input placeholder="Search ticker or company..." value={q} onChange={(e) => setQ(e.target.value)}
                 className="max-w-xs rounded-full" data-testid="ipos-search" />
        </div>
      </Card>

      {loading ? (
        <Card className="p-16 text-center border-border rounded-xl text-sm text-muted-foreground">Loading calendar…</Card>
      ) : !rows.length ? (
        <Card className="p-16 text-center border-border rounded-xl" data-testid="ipos-empty">
          <div className="text-sm text-muted-foreground">Nothing here yet — check another tab or clear your search.</div>
        </Card>
      ) : (
        <Card className="rounded-xl border-border overflow-hidden">
          <div className="hidden md:grid grid-cols-12 text-[10px] uppercase tracking-wider text-muted-foreground px-5 py-2 bg-secondary/30">
            <div className="col-span-3">Ticker · Company</div>
            <div className="col-span-2">Exchange</div>
            <div className="col-span-1 text-right">Shares</div>
            <div className="col-span-2 text-right">{tab === "upcoming" ? "Price range" : tab === "recent" ? "Offer" : "Filed"}</div>
            <div className="col-span-2 text-right">Deal size</div>
            <div className="col-span-1 text-right">Date</div>
            <div className="col-span-1 text-right">Alert</div>
          </div>
          <div className="divide-y divide-border">
            {rows.map((r, i) => (
              <div key={`${r.ticker}-${r.company}-${i}`}
                   className="grid grid-cols-2 md:grid-cols-12 gap-y-1 items-center px-5 py-3 hover:bg-secondary/40"
                   data-testid={`ipo-row-${r.ticker || i}`}>
                <div className="md:col-span-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-tab font-medium text-sm">{r.ticker || "—"}</span>
                    <a href={`https://www.google.com/search?q=${encodeURIComponent((r.company || r.ticker) + " IPO")}`}
                       target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"
                       data-testid={`ipo-link-${r.ticker || i}`}>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{r.company}</div>
                </div>
                <div className="hidden md:block md:col-span-2 text-xs text-muted-foreground">{r.exchange || "—"}</div>
                <div className="md:col-span-1 text-right text-xs md:text-sm font-mono-tab">
                  <span className="md:hidden text-muted-foreground">Shares </span>{fmtShares(r.shares)}
                </div>
                <div className="md:col-span-2 text-right font-mono-tab text-sm">
                  {tab === "upcoming" && (r.price_low != null || r.price_high != null)
                    ? (r.price_low === r.price_high || !r.price_high
                        ? fmtMoney(r.price_low)
                        : `${fmtMoney(r.price_low)}–${fmtMoney(r.price_high)}`)
                    : tab === "recent" ? fmtMoney(r.offer_price) : "—"}
                </div>
                <div className="md:col-span-2 text-right font-mono-tab text-sm text-muted-foreground">
                  {r.notional != null ? fmtMoney(r.notional, 0) : "—"}
                </div>
                <div className="col-span-2 md:col-span-1 text-right text-xs text-muted-foreground">
                  {prettyDate(r.expected_date || r.priced_date || r.filed_date)}
                </div>
                <div className="md:col-span-1 text-right flex items-center justify-end gap-1">
                  {tab === "upcoming" && (
                    <button
                      onClick={() => {
                        setBuyRow(r);
                        const midPrice = r.price_high && r.price_low
                          ? (r.price_high + r.price_low) / 2
                          : (r.price_low || r.price_high || 0);
                        setBuyPrice(midPrice ? String(midPrice) : "");
                        setBuyQty("");
                        setBuyStop("15");
                      }}
                      data-testid={`ipo-buy-${r.ticker || i}`}
                      title="Paper buy at expected price"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-[hsl(var(--gain))]/40 text-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/10 transition-colors">
                      <ShoppingCart className="w-3 h-3" />
                      <span className="hidden md:inline">Buy</span>
                    </button>
                  )}
                  {tab !== "recent" && (
                    <button onClick={() => toggleAlert(r)}
                            data-testid={`alert-btn-${r.ticker || i}`}
                            title={r.is_watched ? "Remove alert" : "Alert me before pricing"}
                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition-colors ${r.is_watched ? "border-[hsl(var(--ai))]/40 text-[hsl(var(--ai))] bg-[hsl(var(--ai))]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      {r.is_watched ? <BellRing className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="text-[11px] text-muted-foreground pt-2">
        Data from Nasdaq's public IPO calendar. Upcoming = expected to price soon · Recently priced = last ~30 days · Filed = new S-1 registrations.
      </div>

      {/* Day-1 paper buy dialog */}
      <AlertDialog open={!!buyRow} onOpenChange={(o) => !o && setBuyRow(null)}>
        <AlertDialogContent data-testid="ipo-buy-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              Day-1 paper buy — {buyRow?.ticker || buyRow?.company}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Locks in a paper order at the expected price so you can practice the debut. Size is auto-computed from your risk profile — override anything below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
              <div>Company<div className="text-foreground text-sm truncate">{buyRow?.company || "—"}</div></div>
              <div>Exchange<div className="text-foreground text-sm">{buyRow?.exchange || "—"}</div></div>
              <div>Expected date<div className="text-foreground text-sm">{buyRow?.expected_date || "—"}</div></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Entry price ($)</div>
                <Input type="number" min="0" step="0.01" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)}
                       data-testid="ipo-buy-price" className="mt-1 font-mono-tab" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Qty (auto if blank)</div>
                <Input type="number" min="0" step="1" value={buyQty} onChange={(e) => setBuyQty(e.target.value)}
                       placeholder="from risk"
                       data-testid="ipo-buy-qty" className="mt-1 font-mono-tab" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Stop distance %</div>
                <Input type="number" min="1" max="90" step="1" value={buyStop} onChange={(e) => setBuyStop(e.target.value)}
                       data-testid="ipo-buy-stop" className="mt-1 font-mono-tab" />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground bg-secondary/40 rounded-md p-2">
              We'll size the position so your max loss (if the stop hits) equals your default per-trade risk. IPOs are extra volatile, so keep the stop wide.
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ipo-buy-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="ipo-buy-confirm"
              disabled={buying}
              onClick={async (e) => {
                e.preventDefault();
                setBuying(true);
                try {
                  const body = {
                    ticker: buyRow.ticker || (buyRow.company || "IPO").split(" ")[0].toUpperCase(),
                    company: buyRow.company || "",
                    expected_price: parseFloat(buyPrice),
                    stop_pct: parseFloat(buyStop) || 15,
                  };
                  if (buyQty) body.quantity = parseFloat(buyQty);
                  const { data: res } = await api.post("/ipos/paper-buy", body);
                  toast.success(`Bought ${res.order.quantity} ${res.order.ticker} @ $${res.order.price} — logged to Journal`);
                  refreshUser?.();
                  setBuyRow(null);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Paper buy failed");
                } finally {
                  setBuying(false);
                }
              }}
              className="bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black">
              {buying ? "Buying…" : "Paper buy"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
